import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { calculateWin, getSideString, getSideStringProps } from '@/core/utils';
import { signOdds } from '@/core/oddsFormatUtils';
import { commissionDiscounts } from '@/consts/constants';

// Both time columns are rendered in UTC for unambiguous record-keeping, so the
// export reads the same regardless of the viewer's local timezone. The
// Timezone column below is a constant label reflecting this.
dayjs.extend(utc);

const TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const TRADE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const EXPORT_TIMEZONE_LABEL = 'UTC';
const DEFAULT_COMMISSION_RATE = 0.01;

const SETTLED_OUTCOMES = new Set([
  'winner',
  'loser',
  'half-win',
  'half-loss',
  'refunded',
]);

export const BET_CSV_HEADERS = [
  { key: 'time', label: 'Time' },
  { key: 'tradeTime', label: 'Trade Time' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'league', label: 'League' },
  { key: 'participants', label: 'Participants' },
  { key: 'side', label: 'Side' },
  { key: 'risk', label: 'Risk' },
  { key: 'win', label: 'Win' },
  { key: 'odds', label: 'Odds' },
  { key: 'result', label: 'Result' },
  { key: 'commission', label: 'Commission Paid' },
];

function formatParticipants(game) {
  if (!game) return '';
  const participants = game.participants || [];
  const isProps = game.league && game.league.includes('PROPS');
  if (participants.length >= 2 && !isProps) {
    const away = participants[0]?.longName ?? '';
    const home = participants[1]?.longName ?? '';
    return `${away} @ ${home}`;
  }
  return game.eventName || '';
}

function formatMoney(value) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

function formatSignedMoney(value) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return '';
  if (n > 0) return `+${n.toFixed(2)}`;
  if (n < 0) return n.toFixed(2);
  return '0.00';
}

/**
 * Gross P&L for a settled bet — matches the backend's `net` field which
 * tracks P&L before commission (commission is tracked separately).
 */
function grossResult(outcome, rawBet, rawWin) {
  const bet = Number.parseFloat(rawBet) || 0;
  const win = Number.parseFloat(rawWin) || 0;

  switch (outcome) {
    case 'winner':
      return win;
    case 'loser':
      return -bet;
    case 'half-win':
      return win / 2;
    case 'half-loss':
      return -bet / 2;
    case 'refunded':
      return 0;
    default:
      return null;
  }
}

/**
 * Effective commission percent for a league+betType, capped by the
 * player's base rate. Mirrors backend logic: if a discount exists for
 * this combo (e.g. MLB moneyline at 0.0075), use the lesser of it and
 * the player's rate.
 */
function toNumber(val) {
  if (val == null) return NaN;
  if (typeof val === 'number') return val;
  if (typeof val === 'object' && val.$numberDecimal != null) {
    return Number.parseFloat(val.$numberDecimal);
  }
  return Number.parseFloat(val);
}

function effectiveCommissionPct(league, betType, baseRate) {
  const parsed = toNumber(baseRate);
  const base = Number.isFinite(parsed) ? parsed : DEFAULT_COMMISSION_RATE;
  const lc = (league || '').toLowerCase();
  if (commissionDiscounts[lc]?.[betType]) {
    return Math.min(commissionDiscounts[lc][betType], base);
  }
  return base;
}

/**
 * Per-bet commission paid. Maker (offer-origin) bets pay no commission.
 * For takers:
 *   winner   -> win × pct          (taken from winnings)
 *   loser    -> min(risk, win) × pct (taken from losing side)
 *   half-win -> (win/2) × pct
 *   half-loss-> (min(risk, win)/2) × pct
 *   refunded -> 0
 */
function commissionPaidFor(outcome, rawBet, rawWin, commissionPct, isOffer) {
  if (isOffer) return 0;
  const bet = Number.parseFloat(rawBet) || 0;
  const win = Number.parseFloat(rawWin) || 0;
  const minSide = Math.min(bet, win);

  switch (outcome) {
    case 'winner':
      return win * commissionPct;
    case 'loser':
      return minSide * commissionPct;
    case 'half-win':
      return (win / 2) * commissionPct;
    case 'half-loss':
      return (minSide / 2) * commissionPct;
    case 'refunded':
      return 0;
    default:
      return 0;
  }
}

function safeSideString(game, session) {
  if (!game) return '';
  try {
    const isProps = game.league && game.league.includes('PROPS');
    return isProps
      ? getSideStringProps(game, session)
      : getSideString(game, session);
  } catch {
    return '';
  }
}

function mapSessionToRow(session, commissionRate) {
  const rawBet = Number.parseFloat(session.bet) || 0;
  const rawWin = calculateWin(session.bet, session.odds);
  const odds = signOdds(session.odds);
  const net = grossResult(session.outcome, rawBet, rawWin);
  const isOffer = session.origin === 'offer';
  const pct = effectiveCommissionPct(
    session.game?.league,
    session.type,
    commissionRate,
  );
  const commission = commissionPaidFor(
    session.outcome,
    rawBet,
    rawWin,
    pct,
    isOffer,
  );

  // Trade time = when the bet was actually matched/placed. Mirrors the rest
  // of the app (SingleBet, MoreBetInformationModal), which fall back to
  // createdAt when matchedTime is absent (e.g. an offer that never filled).
  const tradeTimeRaw = session.matchedTime || session.createdAt;

  return {
    time: session.game?.start
      ? dayjs.utc(session.game.start).format(TIME_FORMAT)
      : '',
    tradeTime: tradeTimeRaw
      ? dayjs.utc(tradeTimeRaw).format(TRADE_TIME_FORMAT)
      : '',
    timezone: EXPORT_TIMEZONE_LABEL,
    league: session.game?.league || '',
    participants: formatParticipants(session.game),
    side: safeSideString(session.game, session),
    risk: formatMoney(rawBet),
    win: formatMoney(rawWin),
    odds: odds || '',
    result: net === null ? '' : formatSignedMoney(net),
    commission: commission === 0 ? '0.00' : `-${commission.toFixed(2)}`,
  };
}

/**
 * Convert a list of bet sessions (as returned by /user/getDailyBets) into rows
 * ready for CSV serialization. Skips rows without a settled outcome.
 *
 * Risk, Win, and Result use raw amounts (no commission adjustment) so that
 * summing the Result column matches the backend's daily `net` field.
 * Commission Paid is computed separately per bet using the player's rate.
 *
 * @param {Array} sessions
 * @param {number} [commissionRate] - the bettor's commissionCharged
 *   (e.g. 0.01 for 1%). Falls back to 1% if omitted.
 */
export function buildBetCsvRows(sessions, commissionRate) {
  const rate = commissionRate || DEFAULT_COMMISSION_RATE;
  const rows = [];
  for (const session of sessions || []) {
    if (!session || !SETTLED_OUTCOMES.has(session.outcome)) continue;
    rows.push(mapSessionToRow(session, rate));
  }
  rows.sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0));
  return rows;
}
